import { memo, useCallback, useRef, useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import axios, { Canceler } from 'axios';

import Button, { ButtonKind } from 'bloko/blocks/button';
import { H2 } from 'bloko/blocks/header';
import InputText, { InputChangeHandler } from 'bloko/blocks/inputText';
import Loading, { LoadingScale } from 'bloko/blocks/loading';
import Modal, { ModalHeader, ModalContent, ModalError, ModalFooter } from 'bloko/blocks/modal';
import Suggest, { SuggestLayer } from 'bloko/blocks/suggest';
import { DataProvider, DataProviderItem } from 'bloko/blocks/suggest/types';
import VSpacing from 'bloko/blocks/vSpacing';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';
import TreeCollection from 'bloko/common/tree/treeCollection';
import { fromTree } from 'bloko/common/tree/treeCollectionHelper';
import { ModelData } from 'bloko/common/tree/types';

import loadPriceRegions from 'src/api/price/loadPriceRegions';
import translation from 'src/components/translation';
import useOnOffState from 'src/hooks/useOnOffState';
import { AppDispatch } from 'src/models/appDispatch';
import { PriceDataTab } from 'src/models/price/priceData';
import fetcher from 'src/utils/fetcher';

import RegionsList, { RegionsListAdditional } from 'src/components/Price/RegionSelectorPopup/RegionsList';

const DEFAULT_INPUT_VALUE = '';
const DEFAULT_SUGGESTED_COLLECTION = fromTree<RegionsListAdditional>([]);

interface RegionSelectorPopupSubmitParams {
    inputValue: string;
    selectedId: string | null;
    collection: TreeCollection<RegionsListAdditional>;
}

export type RegionSelectorPopupSubmit = ({
    inputValue,
    selectedId,
    collection,
}: RegionSelectorPopupSubmitParams) => void;

export interface PriceRegionsParams {
    tabName: PriceDataTab;
}

interface RegionSelectorPopupProps {
    allPriceRegions: ModelData<RegionsListAdditional>[];
    dataProvider: DataProvider;
    defaultSelectedId: string | null;
    popupVisible: boolean;
    title: string;
    onClose: () => void;
    onRadioChange?: (inputValue: string, selectedId: string) => void;
    onSubmit: RegionSelectorPopupSubmit;
    onSuggestChange?: (inputValue: string) => void;
    priceRegionsParams?: PriceRegionsParams;
}

const TrlKeys = {
    error: 'employer.price.resume-access.commonError',
    placeholder: 'price.region-popup.placeholder',
    submit: 'employer.price2.region.pick.button',
};

const RegionSelectorPopup: TranslatedComponent<RegionSelectorPopupProps> = ({
    allPriceRegions,
    dataProvider,
    defaultSelectedId,
    popupVisible,
    title,
    onClose,
    onRadioChange,
    onSubmit,
    onSuggestChange,
    priceRegionsParams,
    trls,
}) => {
    const dispatch: AppDispatch = useDispatch();
    const cancelFetchRef = useRef<Canceler | null>(null);

    const [inputValue, setInputValue] = useState(DEFAULT_INPUT_VALUE);
    const [suggestedCollection, setSuggestedCollection] = useState(DEFAULT_SUGGESTED_COLLECTION);
    const [selectedId, setSelectedId] = useState(defaultSelectedId);
    const [loading, turnLoadingOn, turnLoadingOff] = useOnOffState(false);
    const [error, showError, hideError] = useOnOffState(false);

    const collection = useMemo(
        () => (inputValue ? suggestedCollection : fromTree(allPriceRegions)),
        [allPriceRegions, inputValue, suggestedCollection]
    );

    const reset = useCallback(() => {
        setSuggestedCollection(DEFAULT_SUGGESTED_COLLECTION);
        hideError();
        cancelFetchRef.current?.();
    }, [hideError]);

    useEffect(() => {
        if (popupVisible) {
            setInputValue(DEFAULT_INPUT_VALUE);
            setSelectedId(defaultSelectedId);
            reset();
        }
    }, [defaultSelectedId, popupVisible, reset]);

    const onInputChange = useCallback<InputChangeHandler>(
        (value) => {
            setInputValue(value);
            setSelectedId(null);
            reset();
        },
        [reset]
    );

    const handleSuggestChange = useCallback(
        (item: DataProviderItem) => {
            if (item) {
                onSuggestChange?.(item.text);
                setInputValue(item.text);
                turnLoadingOn();
                const areaId = item.id!;
                const cancelToken = new axios.CancelToken((cancel) => {
                    cancelFetchRef.current = cancel;
                });
                dispatch(loadPriceRegions({ areaId, cancelToken, ...priceRegionsParams }))
                    .then(
                        ({ priceRegions }) => {
                            setSuggestedCollection(fromTree(priceRegions));
                        },
                        (error) => {
                            if (fetcher.isCancel(error)) {
                                return;
                            }
                            showError();
                            console.error(error);
                        }
                    )
                    .finally(() => {
                        turnLoadingOff();
                    });
            }
        },
        [dispatch, onSuggestChange, priceRegionsParams, showError, turnLoadingOff, turnLoadingOn]
    );

    const handleRadioChange = useCallback(
        (id: string) => {
            onRadioChange?.(inputValue, id);
            setSelectedId(id);
        },
        [inputValue, onRadioChange]
    );

    const handleSubmit = useCallback(() => {
        // Если мы в инпут что-то вводили, то коллекция получена с бека, передаем ее, чтобы не ходить повторно,
        // иначе мы выбрали 1 регион из списка всех регионов, передаем только его
        const regions = selectedId ? [collection.getModel(selectedId)] : [];
        const regionsCollection = inputValue ? suggestedCollection : fromTree(regions as ModelData[]);
        onSubmit({ inputValue, selectedId, collection: regionsCollection });
    }, [collection, inputValue, onSubmit, selectedId, suggestedCollection]);

    return (
        <Modal visible={popupVisible} onClose={onClose}>
            <ModalHeader>
                <H2>{title}</H2>
                <VSpacing base={5} />
                <Suggest
                    dataProvider={dataProvider}
                    layer={SuggestLayer.AboveOverlayContent}
                    value={{ text: inputValue }}
                    onChange={handleSuggestChange}
                    autoSelect={true}
                    selectOnBlur={true}
                >
                    <InputText
                        placeholder={trls[TrlKeys.placeholder]}
                        value={inputValue}
                        onChange={onInputChange}
                        data-qa="custom-region__popup_suggest"
                    />
                </Suggest>
            </ModalHeader>
            <ModalContent>
                <div className="price-popup-list" data-qa="resume-access-popup-items">
                    {loading ? (
                        <Loading scale={LoadingScale.Small} />
                    ) : (
                        <>
                            {!error && (
                                <RegionsList
                                    collection={collection}
                                    selectedId={selectedId}
                                    // Заголовок не рисуется, когда показывается список всех вариантов
                                    showTitle={!!inputValue}
                                    onChange={handleRadioChange}
                                />
                            )}
                        </>
                    )}
                </div>
            </ModalContent>
            <ModalError visible={error}>{trls[TrlKeys.error]}</ModalError>
            <ModalFooter>
                <Button
                    kind={ButtonKind.Primary}
                    disabled={loading}
                    onClick={handleSubmit}
                    data-qa="price-popup-region-submit"
                >
                    {trls[TrlKeys.submit]}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default memo(translation(RegionSelectorPopup));
